<script lang="ts">
  // svelte/kit
  import { fade } from 'svelte/transition';
  // UI components
  import * as AlertDialog from '$lib/components/ui/alert-dialog/index.js';
  import { Button } from '$lib/components/ui/button';
  import { Input } from '$lib/components/ui/input/index.js';
  import { Label } from '$lib/components/ui/label/index.js';
  import * as Select from '$lib/components/ui/select/index.js';
  // RoastLog
  import logger from '$lib/logger';
  import {
    beans,
    getBeanNameById,
    roastables,
    roastQueue,
    userProfile
  } from '$lib/PersistedState.svelte';
  import { floatToDecimal } from '$lib/RLogUtils';
  import type { Bean, Component, Nullable, Roastable, SelectChoice } from '$lib/types/RoastLog';
  // Other
  import { Plus, Trash2 } from '@lucide/svelte';
  import { toast } from 'svelte-sonner';

  const errorClass = 'border border-rose-600 dark:border-red-800 dark:bg-gray-800';

  let {
    open = $bindable(true),
    qItemIndex
  }: {
    open: boolean;
    qItemIndex: number;
  } = $props();

  // make a copy of the state
  // svelte-ignore state_referenced_locally
  const qItem = $state.snapshot(roastQueue.at(qItemIndex));

  // The caller of this component checks that the roastable isn't undefined, so it's safe to force
  // typescript to yield here with the `!`.
  let roastable: Roastable = $state(qItem.roastable)!;
  let totalPercent = $state(100);
  let errorMessage: Nullable<string> = $state(null);
  let resetDisabled = $state(true);
  let saveDisabled = $state(true);

  // Fetch the roastable from the roastables global state. This is used for the reset.
  const originalRoastable = roastable.id
    ? roastables.current.find((r) => r.id === roastable.id)
    : null;

  let originalComponents = originalRoastable?.components
    ? $state.snapshot(originalRoastable.components)
    : [];

  let beanChoices: SelectChoice[] = $derived.by(() => {
    const usedBeanIds = new Set(roastable.components.map((c) => c.bean_id));

    return beans.current
      .filter((b) => !usedBeanIds.has(b.id))
      .map(
        (b: Bean): SelectChoice => ({
          value: b.id.toString(),
          label: b.name.trim()
        })
      );
  });

  const handleSave = () => {
    logger.info('Saving change to roastable recipe');
    // This updates the roastQueue store
    roastQueue.updateItemAt(qItemIndex, {
      roastable: {
        ...roastable,
        components: roastable.components
      }
    });

    toast.success(`Succesfully updated ${roastable.name} recipe.`);
    setTimeout(() => {
      open = false;
    }, 500);
  };

  const handleReset = () => {
    logger.info('Resetting roastable components', { originalComponents });

    // Reset UI
    roastable.components = originalComponents;
    // Reset global state
    roastQueue.updateItemAt(qItemIndex, {
      roastable: {
        ...roastable,
        components: originalComponents
      }
    });

    toast.success(`Reset ${roastable.name} to its original recipe.`);
    setTimeout(() => {
      open = false;
    }, 250);
  };

  const handleRoastableComponentChanged = (componentIndex: number, newBeanId: string) => {
    logger.info('Changing roast queue component', {
      componentIndex,
      qItemIndex,
      newBeanId
    });

    const updatedComponents = roastable.components.map((component, i) =>
      i === componentIndex ? { ...component, bean_id: parseInt(newBeanId) } : component
    );
    // update the internal state which triggers the UI update.
    roastable.components = updatedComponents;

    resetDisabled = false;
    saveDisabled = false;
  };

  const handlePercentChanged = (
    index: number,
    e: Event & { currentTarget: EventTarget & HTMLInputElement }
  ): any => {
    const newPercent = floatToDecimal(parseFloat(e.currentTarget.value));

    // Protect against invalid inputs
    if (isNaN(newPercent) || newPercent < 1) {
      e.currentTarget.value = roastable.components[index].percent.toString();
      return;
    }

    // Apply max constraints
    const maxAllowed = roastable.components.length > 1 ? 99 : 100;
    if (newPercent > maxAllowed) {
      e.currentTarget.value = maxAllowed.toString();
      return;
    }

    const oldPercent = roastable.components[index].percent;
    const percentDelta = oldPercent - newPercent;

    logger.info('Changing roast queue component percentage', {
      componentIndex: index,
      qItemIndex,
      newPercent,
      percentDelta
    });

    // Find component to adjust (prefer the first one that's not being edited)
    let adjustIndex = index === 0 ? 1 : 0;

    let newComponents: Component[] = [];

    // Check if adjusting this component would make it go below 1%
    if (roastable.components[adjustIndex].percent + percentDelta < 1) {
      // We need to distribute the change across multiple components
      const remaining = percentDelta + roastable.components[adjustIndex].percent - 1;

      // Create new components array with initial adjustments
      newComponents = roastable.components.map((c: Component, i: number) => {
        if (i === index) {
          return { ...c, percent: newPercent };
        } else if (i === adjustIndex) {
          // Set this component to minimum 1%
          return { ...c, percent: 1 };
        } else {
          return c;
        }
      });

      // Distribute remaining difference among other components
      let remainingToDistribute = remaining;

      // Skip the component being edited and the one we already set to 1%
      for (let i = 0; i < newComponents.length && remainingToDistribute !== 0; i++) {
        if (i !== index && i !== adjustIndex) {
          // Calculate how much we can subtract from this component
          const available = Math.max(0, newComponents[i].percent - 1);
          const toSubtract = Math.min(available, remainingToDistribute); // This is negative

          logger.info(`Adjusting compoment index ${i}`, {
            available,
            toSubtract,
            remainingToDistribute
          });

          newComponents[i].percent = floatToDecimal(newComponents[i].percent + toSubtract);
          remainingToDistribute = floatToDecimal(remainingToDistribute - toSubtract);
        }
      }

      // If we still have remaining difference, we need to adjust our edited value
      if (remainingToDistribute !== 0) {
        newComponents[index].percent = floatToDecimal(
          newComponents[index].percent + remainingToDistribute
        );
        e.currentTarget.value = newComponents[index].percent.toString();
      }
    } else {
      // Simple case: just adjust one other component
      newComponents = roastable.components.map((c: Component, i: number) => {
        if (i === index) {
          return { ...c, percent: newPercent };
        } else if (i === adjustIndex) {
          return { ...c, percent: floatToDecimal(c.percent + percentDelta) };
        } else {
          return c;
        }
      });
    }

    roastable.components = newComponents;
    totalPercent = calculateTotalPercent(roastable.components);
    setStateByPercent(totalPercent);
  };

  const addNewComponent = () => {
    logger.info('Adding new roastable component');
    // Grab the first bean choice since those already filter out the ones that have been used.
    const b = beanChoices[0];
    const newComponents = [
      ...roastable.components,
      {
        bean_id: parseInt(b.value),
        bean_name: b.label,
        percent: 1
      }
    ];
    // Deduct by 1
    newComponents[0].percent--;

    roastable.components = newComponents;
    totalPercent = calculateTotalPercent(newComponents);
    setStateByPercent(totalPercent);
  };

  const deleteComponentAt = (index: number) => {
    logger.info(`Deleting roastable component at index ${index}`);
    const deletedComponent = roastable.components[index];
    const newComponents = roastable.components.filter((_, i) => i !== index);
    newComponents[0].percent += deletedComponent.percent;

    roastable.components = newComponents;
    totalPercent = calculateTotalPercent(newComponents);
    setStateByPercent(totalPercent);
  };

  const calculateTotalPercent = (components: Component[]) =>
    floatToDecimal(components.reduce((sum, c) => sum + c.percent, 0));

  const setStateByPercent = (pct: number) => {
    if (pct !== 100) {
      errorMessage = `Components must add to 100%, your adds to ${pct}%`;
      saveDisabled = true;
    } else {
      errorMessage = null;
      saveDisabled = false;
    }
    resetDisabled = false;
  };
</script>

<AlertDialog.Root bind:open>
  <AlertDialog.Content class="sm:max-w-[700px]">
    <AlertDialog.Header>
      <AlertDialog.Title
        >Edit recipe for {roastable.name}
        {#if qItem.startingMass}
          ({qItem.startingMass} {userProfile.current?.profile.mass_scale})
        {/if}
      </AlertDialog.Title>
    </AlertDialog.Header>
    <div class="mt-4 grid grid-cols-5 items-center gap-4">
      <Label class="col-span-3">Component</Label>
      <Label>Percent</Label>
      <Label>Amount</Label>
      {#each roastable.components as component, i}
        <div class="col-span-3">
          <Select.Root
            type="single"
            onValueChange={(beanId) => handleRoastableComponentChanged(i, beanId)}
            value={component.bean_id.toString()}
          >
            <Select.Trigger>{getBeanNameById(component.bean_id)}</Select.Trigger>
            <Select.Content>
              {#each beanChoices as bean}
                <Select.Item value={bean.value}>
                  {bean.label}
                </Select.Item>
              {/each}
            </Select.Content>
          </Select.Root>
        </div>
        <div class="flex flex-row items-center justify-center gap-2">
          <div class="relative flex items-center">
            <Input
              type="number"
              class={`col-span-1 w-20 flex-1 ${errorMessage !== null ? errorClass : ''}`}
              value={component.percent}
              min="1"
              max={roastable.components.length > 1 ? '99' : '100'}
              onchange={(e) => handlePercentChanged(i, e)}
              disabled={roastable.components.length < 2}
            />
            <div class="text-muted-foreground pointer-events-none">%</div>
          </div>
        </div>

        <div>
          {#if qItem.startingMass}
            {((qItem.startingMass * component.percent) / 100).toFixed(1)}
            {userProfile.current?.profile.mass_scale}
          {/if}
          {#if roastable.components.length > 1}
            <Button size="icon" variant="outline" class="ml-2" onclick={() => deleteComponentAt(i)}>
              <Trash2 color="red" />
            </Button>
          {/if}
        </div>
      {/each}
    </div>
    <div class="justify-left">
      <Button size="icon" variant="outline" onclick={addNewComponent}>
        <Plus color="green" />
      </Button>
    </div>

    {#if errorMessage}
      <div class="text-red-600 dark:text-red-400" transition:fade={{ duration: 200 }}>
        {errorMessage}
      </div>
    {/if}
    <AlertDialog.Footer>
      {#if resetDisabled}
        <Button type="submit" onclick={() => (open = false)} variant="destructive">Cancel</Button>
      {:else}
        <Button type="submit" onclick={handleReset} variant="destructive" disabled={resetDisabled}
          >Reset</Button
        >
      {/if}
      <Button type="submit" onclick={handleSave} disabled={saveDisabled}>Save changes</Button>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>
